import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AppNotification, PushInput } from './notification';


type NotificationsState = {
    items      : AppNotification[];
    pulse      : number;
    push       : (input: PushInput) => void;
    hydrate    : (items: AppNotification[]) => void;
    markRead   : (id: string) => void;
    markUnread : (id: string) => void;
    markAllRead: () => void;
    dismiss    : (id: string) => void;
    clear      : () => void;
};


export const useNotificationsStore = create<NotificationsState>()(
    devtools(
        (set) => ({
            items: [],
            pulse: 0,

            push: (input) => set((state) => {
                const exists = state.items.some((n) => n.id === input.id);

                // идемпотентно по id: при повторном пуше сохраняем read, пульс не бьём
                if (exists) {
                    return {
                        items: state.items.map((n) =>
                            n.id === input.id ? { ...n, ...input, read: n.read } : n,
                        ),
                    };
                }

                // новый id — добавляем и бьём пульс (колокольчик дёрнется)
                return {
                    items: [ { ...input, read: false }, ...state.items ],
                    pulse: state.pulse + 1,
                };
            }, false, 'notifications/push'),

            // уведомления из профиля: без пульса, локально прочитанное не откатываем
            hydrate: (incoming) => set((state) => {
                const local = new Map(state.items.map((n) => [ n.id, n ]));

                const remote = incoming.map((n) => ({
                    ...n,
                    read  : n.read || Boolean(local.get(n.id)?.read),
                    remote: true,
                }));

                const remoteIds = new Set(remote.map((n) => n.id));

                return { items: [ ...remote, ...state.items.filter((n) => !remoteIds.has(n.id)) ] };
            }, false, 'notifications/hydrate'),

            markRead: (id) => set((state) => ({
                items: state.items.map((n) => n.id === id ? { ...n, read: true } : n),
            }), false, 'notifications/markRead'),

            markUnread: (id) => set((state) => ({
                items: state.items.map((n) => n.id === id ? { ...n, read: false } : n),
            }), false, 'notifications/markUnread'),

            markAllRead: () => set((state) => ({
                items: state.items.map((n) => ({ ...n, read: true })),
            }), false, 'notifications/markAllRead'),

            dismiss: (id) => set((state) => ({
                items: state.items.filter((n) => n.id !== id),
            }), false, 'notifications/dismiss'),

            clear: () => set({ items: [], pulse: 0 }, false, 'notifications/clear'),
        }),
        {
            name   : 'notifications',
            enabled: process.env.NODE_ENV === 'development',
        },
    ),
);


export const selectItems       = (s: NotificationsState) => s.items;
export const selectUnreadCount = (s: NotificationsState) => s.items.filter((n) => !n.read).length;
export const selectPulse       = (s: NotificationsState) => s.pulse;
